import {
  formatLocalDate,
  formatRatePercent,
  formatTimestamp,
  formatToman,
  statusLabel,
  statusValues,
} from '@saay/formatting';
import {
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  FactList,
  LoadMore,
  PageHeader,
  Section,
  SheetSection,
  Skeleton,
  StatusChip,
  Tabs,
} from '../kit.js';
import { ListRow } from '@saay/blocks';
import { ReceiptTextIcon } from 'lucide-react';
import { Link, Outlet, useNavigate, useParams, useSearchParams } from 'react-router';

import type { AdminPayment, AdminReturnRequest, AdminReturnRequestSummary } from '../api.js';
import { AdminListTable, type DataTableColumn } from '../components/admin-list-table.js';
import { FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { QueryState, RequireArea, usePageTitle } from '../components/guard.js';
import { ListFooter } from '../components/list-footer.js';
import { onSheetOpenChange, OPENED_FROM_LIST, useCloseSheet } from '../components/route-sheet.js';
import {
  listStatus,
  type PaymentFilters,
  type ReturnRequestFilters,
  useAdminPayment,
  useAdminPayments,
  useAdminReturnRequest,
  useAdminReturnRequests,
} from '../queries.js';

type AreaTab = 'payments' | 'returns';

function AreaTabs({ value }: { readonly value: AreaTab }) {
  const navigate = useNavigate();
  return (
    <Tabs<AreaTab | 'delinquency'>
      label="بخش وصول"
      onChange={(next) =>
        void navigate(
          next === 'returns'
            ? '/return-requests'
            : next === 'delinquency'
              ? '/delinquency-cases'
              : '/payments',
        )
      }
      panel={value === 'returns' ? <ReturnsList /> : <PaymentsList />}
      tabs={[
        { key: 'payments', label: 'پرداخت‌ها' },
        { key: 'returns', label: 'مرجوعی‌ها' },
        { key: 'delinquency', label: 'دیرکرد' },
      ]}
      value={value}
    />
  );
}

function CollectionsFrame({ tab }: { readonly tab: AreaTab }) {
  usePageTitle('اقساط، پرداخت و دیرکرد');
  return (
    <RequireArea area="payments">
      {() => (
        <>
          <PageHeader
            description="پرداخت‌های خریداران و درخواست‌های مرجوعی، فقط برای مشاهده."
            title="اقساط، پرداخت و دیرکرد"
          />
          <AreaTabs value={tab} />
          <Outlet />
        </>
      )}
    </RequireArea>
  );
}

export function PaymentsPage() {
  return <CollectionsFrame tab="payments" />;
}

export function ReturnRequestsPage() {
  return <CollectionsFrame tab="returns" />;
}

function paymentBuyerCell(buyer: AdminPayment['buyer']) {
  return (
    <span className="saay-cell-main">
      <Link to={`/buyers/${buyer.buyerId}`}>{buyer.displayName}</Link>
      <small>
        <bdi dir="ltr" translate="no">
          {buyer.mobileMasked}
        </bdi>
      </small>
    </span>
  );
}

function PaymentsList() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const status = params.get('status') ?? '';
  const filters: PaymentFilters = status === '' ? {} : { status };
  const list = useAdminPayments(filters);
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const columns: readonly DataTableColumn<AdminPayment>[] = [
    {
      header: 'مبلغ',
      id: 'amount',
      pin: 'start',
      render: (row) => (
        <Link state={OPENED_FROM_LIST} to={`/payments/${row.paymentAttemptId}`}>
          {formatToman(row.amountIrr)}
        </Link>
      ),
    },
    {
      header: 'کارمزد پرداخت',
      id: 'fee',
      render: (row) => formatToman(row.paymentFeeIrr),
    },
    {
      header: 'وضعیت',
      id: 'status',
      render: (row) => <StatusChip enumName="paymentAttemptStatus" value={row.status} />,
    },
    {
      header: 'زمان تأیید',
      id: 'verified',
      render: (row) =>
        row.verifiedAt === null ? (
          '—'
        ) : (
          <time dateTime={row.verifiedAt}>{formatTimestamp(row.verifiedAt)}</time>
        ),
    },
    { header: 'خریدار', id: 'buyer', render: (row) => paymentBuyerCell(row.buyer) },
  ];
  return (
    <Section id="payment-list" title="فهرست پرداخت‌ها">
      <FilterPillBar
        activeFilterCount={status === '' ? 0 : 1}
        label="فیلتر پرداخت‌ها"
        onClearAll={() => {
          const next = new URLSearchParams(params);
          next.delete('status');
          setParams(next);
        }}
      >
        <FilterPill
          emptyLabel="همه وضعیت‌ها"
          label="وضعیت"
          onChange={(value) => {
            const next = new URLSearchParams(params);
            if (value === '') next.delete('status');
            else next.set('status', value);
            setParams(next);
          }}
          options={statusValues('paymentAttemptStatus').map((value) => ({
            label: statusLabel('paymentAttemptStatus', value),
            value,
          }))}
          value={status}
        />
      </FilterPillBar>
      <QueryState
        empty={
          <EmptyState
            action={
              status === '' ? undefined : (
                <Button onClick={() => setParams({})} variant="secondary">
                  پاک‌کردن فیلتر
                </Button>
              )
            }
            title={status === '' ? 'پرداختی ثبت نشده است' : 'پرداختی با این فیلتر پیدا نشد'}
          />
        }
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری پرداخت‌ها…" lines={4} />}
        status={listStatus(list)}
      >
        <AdminListTable
          caption="فهرست پرداخت‌ها"
          columns={columns}
          mobileSummary={(row) => ({
            primary: formatToman(row.amountIrr),
            secondary: <StatusChip enumName="paymentAttemptStatus" value={row.status} />,
          })}
          onRowClick={(row) =>
            void navigate(`/payments/${row.paymentAttemptId}`, { state: OPENED_FROM_LIST })
          }
          regionLabel="جدول پرداخت‌ها"
          rowKey={(row) => row.paymentAttemptId}
          rows={rows}
        />
        {list.isFetchNextPageError ? (
          <ErrorState error={list.error} onRetry={() => void list.fetchNextPage()} />
        ) : null}
        <ListFooter
          pager={
            <LoadMore
              hasMore={list.hasNextPage}
              loading={list.isFetchingNextPage}
              onLoadMore={() => void list.fetchNextPage()}
            />
          }
          shown={rows.length}
          total={undefined}
        />
      </QueryState>
    </Section>
  );
}

function Facts({ items }: { readonly items: readonly (readonly [string, string] | null)[] }) {
  return (
    <FactList
      items={items
        .filter((item) => item !== null)
        .map(([term, value]) => ({ key: term, label: term, value }))}
    />
  );
}

export function PaymentDetailSheet() {
  const { paymentAttemptId = '' } = useParams();
  const payment = useAdminPayment(paymentAttemptId);
  const close = useCloseSheet('/payments');
  const data = payment.data;
  usePageTitle('پرداخت');
  const fee = data?.paymentFee;
  return (
    <Dialog
      onOpenChange={onSheetOpenChange(close)}
      open
      size="wide"
      title={data === undefined ? 'پرداخت' : formatToman(data.amountIrr)}
    >
      <QueryState
        error={payment.error}
        onRetry={() => void payment.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری پرداخت…" lines={6} />}
        status={payment.status}
      >
        {data === undefined ? null : (
          <div className="admin-sections">
            <SheetSection id="payment-summary" title="پرداخت">
              <Facts
                items={[
                  ['مبلغ', formatToman(data.amountIrr)],
                  ['اصل اقساط', formatToman(data.principalIrr)],
                  ['وجه التزام', formatToman(data.lateFeeIrr)],
                  ['کارمزد پرداخت', formatToman(data.paymentFeeIrr)],
                  ['وضعیت', statusLabel('paymentAttemptStatus', data.status)],
                  ['وضعیت درخواست', statusLabel('paymentIntentStatus', data.intentStatus)],
                  ['ارائه‌دهنده', statusLabel('walletProvider', data.provider)],
                  ['زمان تأیید', data.verifiedAt === null ? '—' : formatTimestamp(data.verifiedAt)],
                ]}
              />
              <div>{paymentBuyerCell(data.buyer)}</div>
              <p>
                هفته‌ها: {data.weekStarts.map((week) => formatLocalDate(week, 'long')).join('، ')}
              </p>
            </SheetSection>
            {fee === undefined ? null : (
              <SheetSection id="payment-fee-applied" title="محاسبهٔ کارمزد پرداخت">
                <Facts
                  items={[
                    ['پایه', formatToman(fee.baseIrr)],
                    ['نرخ', formatRatePercent(fee.rate, { invalid: 'raw' })],
                    ['حداقل کارمزد', formatToman(fee.minimumFeeIrr)],
                    ['حداقل اعمال شد', fee.minimumApplied ? 'بله' : 'خیر'],
                  ]}
                />
              </SheetSection>
            )}
            <SheetSection id="payment-installments" title="اقساط این پرداخت">
              {data.installments.length === 0 ? (
                <EmptyState title="قسطی به این پرداخت وصل نیست" />
              ) : (
                <ul className="flex flex-col gap-2">
                  {data.installments.map((line) => (
                    <li key={line.installmentId}>
                      <ListRow
                        description={`اصل ${formatToman(line.principalIrr)} · وجه التزام ${formatToman(line.lateFeeIrr)}`}
                        icon={ReceiptTextIcon}
                        title={formatLocalDate(line.weekStart, 'long')}
                        tone="clay"
                      />
                    </li>
                  ))}
                </ul>
              )}
            </SheetSection>
          </div>
        )}
      </QueryState>
    </Dialog>
  );
}

function buyerName(buyer: AdminReturnRequestSummary['buyer']): string {
  return buyer.displayNameFa ?? buyer.displayNameMasked;
}

function ReturnsList() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const status = params.get('returnStatus') ?? '';
  const filters: ReturnRequestFilters = status === '' ? {} : { status };
  const list = useAdminReturnRequests(filters);
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const columns: readonly DataTableColumn<AdminReturnRequestSummary>[] = [
    {
      header: 'فروش',
      id: 'sale',
      pin: 'start',
      render: (row) => (
        <span className="saay-cell-main">
          <Link state={OPENED_FROM_LIST} to={`/return-requests/${row.id}`}>
            <bdi dir="ltr" translate="no">
              {row.saleReference}
            </bdi>
          </Link>
          <small>{row.merchant.nameFa}</small>
        </span>
      ),
    },
    { header: 'خریدار', id: 'buyer', render: (row) => buyerName(row.buyer) },
    {
      header: 'نوع',
      id: 'type',
      render: (row) => statusLabel('returnType', row.returnType),
    },
    { header: 'مبلغ', id: 'amount', render: (row) => formatToman(row.reductionIrr) },
    {
      header: 'وضعیت',
      id: 'status',
      render: (row) => <StatusChip enumName="returnRequestStatus" value={row.status} />,
    },
  ];
  return (
    <Section id="return-request-list" title="فهرست مرجوعی‌ها">
      <FilterPillBar
        activeFilterCount={status === '' ? 0 : 1}
        label="فیلتر مرجوعی‌ها"
        onClearAll={() => {
          const next = new URLSearchParams(params);
          next.delete('returnStatus');
          setParams(next);
        }}
      >
        <FilterPill
          emptyLabel="همه وضعیت‌ها"
          label="وضعیت مرجوعی"
          onChange={(value) => {
            const next = new URLSearchParams(params);
            if (value === '') next.delete('returnStatus');
            else next.set('returnStatus', value);
            setParams(next);
          }}
          options={statusValues('returnRequestStatus').map((value) => ({
            label: statusLabel('returnRequestStatus', value),
            value,
          }))}
          value={status}
        />
      </FilterPillBar>
      <QueryState
        empty={<EmptyState title="درخواست مرجوعی ثبت نشده است" />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری مرجوعی‌ها…" lines={4} />}
        status={listStatus(list)}
      >
        <AdminListTable
          caption="فهرست مرجوعی‌ها"
          columns={columns}
          mobileSummary={(row) => ({
            primary: (
              <span className="saay-cell-main">
                <strong>{row.merchant.nameFa}</strong>
                <small>{buyerName(row.buyer)}</small>
              </span>
            ),
            secondary: <StatusChip enumName="returnRequestStatus" value={row.status} />,
          })}
          onRowClick={(row) =>
            void navigate(`/return-requests/${row.id}`, { state: OPENED_FROM_LIST })
          }
          regionLabel="جدول مرجوعی‌ها"
          rowKey={(row) => row.id}
          rows={rows}
        />
        {list.isFetchNextPageError ? (
          <ErrorState error={list.error} onRetry={() => void list.fetchNextPage()} />
        ) : null}
        <ListFooter
          pager={
            <LoadMore
              hasMore={list.hasNextPage}
              loading={list.isFetchingNextPage}
              onLoadMore={() => void list.fetchNextPage()}
            />
          }
          shown={rows.length}
          total={undefined}
        />
      </QueryState>
    </Section>
  );
}

function previewFacts(request: AdminReturnRequest) {
  const preview = request.preview;
  return (
    <Facts
      items={[
        ['مبلغ مرجوعی', formatToman(preview.reductionIrr)],
        ['اصل فعلی', formatToman(preview.currentPrincipalIrr)],
        ['اصل پس از مرجوعی', formatToman(preview.revisedPrincipalIrr)],
        preview.currentSaayFeeIrr === undefined
          ? null
          : ['کارمزد فعلی', formatToman(preview.currentSaayFeeIrr)],
        preview.revisedSaayFeeIrr === undefined
          ? null
          : ['کارمزد پس از مرجوعی', formatToman(preview.revisedSaayFeeIrr)],
        preview.saayFeeReductionIrr === undefined
          ? null
          : ['کاهش کارمزد', formatToman(preview.saayFeeReductionIrr)],
        preview.merchantNetReductionIrr === undefined
          ? null
          : ['کاهش خالص فروشگاه', formatToman(preview.merchantNetReductionIrr)],
      ]}
    />
  );
}

export function ReturnRequestDetailSheet() {
  const { returnRequestId = '' } = useParams();
  const request = useAdminReturnRequest(returnRequestId);
  const close = useCloseSheet('/return-requests');
  const data = request.data;
  usePageTitle('مرجوعی');
  return (
    <Dialog
      onOpenChange={onSheetOpenChange(close)}
      open
      size="wide"
      title={data === undefined ? 'مرجوعی' : data.saleReference}
    >
      <QueryState
        error={request.error}
        onRetry={() => void request.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری مرجوعی…" lines={6} />}
        status={request.status}
      >
        {data === undefined ? null : (
          <div className="admin-sections">
            <SheetSection id="return-summary" title="درخواست مرجوعی">
              <p>
                <StatusChip enumName="returnRequestStatus" value={data.status} />{' '}
                {statusLabel('returnType', data.returnType)}
              </p>
              <Facts
                items={[
                  ['فروشگاه', data.merchant.nameFa],
                  ['شعبه', data.branch.nameFa],
                  ['خریدار', buyerName(data.buyer)],
                  ['دلیل', data.reason],
                  ['مهلت مرجوعی', formatTimestamp(data.returnWindowClosesAt)],
                ]}
              />
              {previewFacts(data)}
            </SheetSection>
            {data.confirmation === null ? null : (
              <SheetSection id="return-confirmation" title="تأیید خریدار">
                <p>
                  <StatusChip
                    enumName="confirmationRequestStatus"
                    value={data.confirmation.status}
                  />{' '}
                  {statusLabel('confirmationMode', data.confirmation.mode)}
                </p>
              </SheetSection>
            )}
            {data.saleAmendment === null ? null : (
              <SheetSection id="return-amendment" title="اصلاحیه">
                <p>
                  {data.saleAmendment.amendmentType === 'FULL_RETURN'
                    ? statusLabel('returnType', 'FULL')
                    : statusLabel('returnType', 'PARTIAL')}{' '}
                  · {formatTimestamp(data.saleAmendment.appliedAt)}
                </p>
              </SheetSection>
            )}
          </div>
        )}
      </QueryState>
    </Dialog>
  );
}
